<script>
import { getPartnerName, pureFilters } from '@/lib/agreement/filters';
import partnerNames from '@/mixins/partnerNames';
import childrenNames from '@/mixins/childrenNames';
import dayOptions from '@/lib/dayOptions';
import ConditionalGroup from '../Form/ConditionalGroup.vue';
import RadioGroup from '../Form/RadioGroup.vue';
import { getYear } from 'date-fns';
import range from 'lodash/range';
import {hasEditPermission, currentRoleLevelName} from '@/mixins/roleLevelPermissions';
import { differenceInYears, parseISO } from 'date-fns';
import { mapState } from 'vuex';

export default {
	components: { ConditionalGroup, RadioGroup },
	filters: {
		formatCurrency: pureFilters.formatCurrency,
	},
	mixins: [partnerNames, childrenNames],
	props: ['agreementData'],
	data() {
		const year = getYear(new Date());
		return { dayOptions, yearOptions: range(year, year + 5),	
			isViewMode : !hasEditPermission('section-child-support'),
			showLearnButton: ['client-level-1', 'client-level-2'].includes(currentRoleLevelName)
		};	
	},
	computed: {
		...mapState(['currentRoleLevel']),
		parenting() {
			return this.agreementData.parenting;
		},
		primaryParent() {
			return getPartnerName(
				this.parenting.primaryParent,
				this.agreementData
			);
		},
		otherParent() {
			return getPartnerName(
				this.parenting.primaryParent,
				this.agreementData,
				true
			);
		},
		childSupport() {
			return this.agreementData.childSupport
			//  = this.agreementData.childSupport.hasOwnProperty('additionalClausesToRecitals') 
			// 	? this.agreementData.childSupport 
			// 	: {...this.agreementData.childSupport, additionalClausesToRecitals: []};
		},
		partner1Payor() {
			return this.partner1Amount > this.partner2Amount;
		},
		payorName() {
			return this.partner1Payor ? this.partner1Name : this.partner2Name;
		},
		payeeName() {
			return this.partner1Payor ? this.partner2Name : this.partner1Name;
		},
		setOff() {
			return Math.abs(this.partner1Amount - this.partner2Amount);
		},
		partner1Amount() {
			return Number(this.childSupport.partner1Amount) || 0;
		},
		partner2Amount() {
			return Number(this.childSupport.partner2Amount) || 0;
		},
		children() {
			return this.agreementData.children;
		},
		childrenUnder18() {
			return (
				this.agreementData.children.filter(child => {
					const {birthdate} = child
					return differenceInYears(new Date(), parseISO(birthdate))  < 18;
				})
			)
		},
		showAgeWarning() {
			return (
				this.agreementData.children &&
				this.agreementData.children.length &&
				this.agreementData.children.every(child => {
					const {birthdate} = child
					return differenceInYears(new Date(), parseISO(birthdate))  >= 19 && child.selfSupporting;
				})
			);
		},
		sections() {
			return this.agreementData.agreement.sections;
		},
		isUserSeparatingParty(){
			return this.currentRoleLevel.roleAssociated === 'party'
		},
	},
	watch: {
		partner1Payor(val) {
			this.childSupport.setOffParty = val ? 0 : 1;
		},
		setOff(val) {
			this.childSupport.setOffAmount = Number(val).toFixed(2);
		},
	},
	mounted(){
		this.agreementData.childSupport = this.agreementData.childSupport.hasOwnProperty('additionalClausesToRecitals') 
				? this.agreementData.childSupport 
				: {...this.agreementData.childSupport, additionalClausesToRecitals: []};
	}
};
</script>

<template>
	<b-card>
		<div slot="header" class="section-heading">
			<h4>Child Support Details</h4>
			<a href="#" onclick="Intercom('showArticle', 11106656)" v-if="this.showLearnButton">Learn &nbsp;<b-icon-book/></a>
		</div>

		<b-alert variant="warning" :show="showAgeWarning">
			<h5 class="alert-heading">Warning!</h5>
			<div>
				Since your Children are over the age of 19 and self-supporting, Child Support is not legally payable.  Please deselect 'Child Support' from the <a href="#matter-details" class="warning-link-a"><b>Matter Details</b></a> section.
			</div>
		</b-alert>
		<conditional-group :condition="!sections.includes('Parenting')">
			<radio-group
				v-model="parenting.arrangement"
				class="mb-3"
				input-cols="8"
				label="What is the Parenting Arrangement?"
				reset-undefined
				select
				:options="[
					{
						value: 'Primary',
						text: 'Primary with Parenting Time for other parent',
					},
					{
						value: 'Primary/Contact',
						text: 'Primary with Contact for other party',
					},
					{ value: 'Shared', text: 'Shared Parenting' },
				]"
				:testid="'child-support-parenting-agreement'"
				:length="'long'"
				:disabled="isViewMode"
			/>

			<conditional-group
				class="my-3"
				:condition="
					parenting.arrangement === 'Primary' ||
					parenting.arrangement === 'Primary/Contact'
				"
			>
				<radio-group
					v-model="parenting.primaryParent"
					:agreement-data="agreementData"
					input-cols="4"
					label="Who is the primary parent?"
					partner
					reset-undefined
					select
					:testid="'child-support-primary-parent'"
					:length="'long'"
					:disabled="isViewMode"
				/>
			</conditional-group>
		</conditional-group>

		<p
			v-if="
				parenting.arrangement === 'Primary' ||
				parenting.arrangement === 'Primary/Contact'
			"
		>
			In this primary parenting situation, {{ primaryParent }} is the
			primary parent.
		</p>

		<conditional-group
			class="my-3"
			:condition="
				parenting.arrangement === 'Primary' ||
				parenting.arrangement === 'Primary/Contact'
			"
			@reset="childSupport.payment = undefined"
		>
			<input-group
				v-model="childSupport.payment"
				:label="`What amount of Child Support must ${otherParent} pay?`"
				dollar
				:testid="'child-support-payment'"
				:disabled="isViewMode"
				toolTipText="Consult an online tool to provide you with child support amounts or have your lawyer provide a calculation.
				You will use the child support amount payable by the non-primary parent."
			/>
		</conditional-group>

		<p v-if="parenting.arrangement === 'Shared'">
			Since this is a shared parenting situation, please provide the child
			support information for both parties.
		</p>

		<conditional-group
			class="my-3"
			:condition="parenting.arrangement === 'Shared'"
			@reset="
				childSupport.partner1Amount = undefined;
				childSupport.partner2Amount = undefined;
				childSupport.setOffParty = undefined;
				childSupport.setOffAmount = undefined;
			"
		>
			<input-group
				v-model="childSupport.partner1Amount"
				class="mb-3"
				:label="`${partner1Name} must pay Child Support amounting to`"
				dollar
				:testid="'child-support-p1-amounting'"
				:disabled="isViewMode"
				toolTipText="Under the <i>Federal Child Support Guidelines</i>, in a shared parenting arrangement each party pays child support
				to the other parent based on their income. Enter the child support amount payable for each parent. "
			/>

			<input-group
				v-model="childSupport.partner2Amount"
				class="mb-3"
				:label="`${partner2Name} must pay Child Support amounting to`"
				input-cols="4"
				dollar
				:testid="'child-support-p2-amounting'"
				:disabled="isViewMode"
				toolTipText="Under the <i>Federal Child Support Guidelines</i>, in a shared parenting arrangement each party pays child support
				to the other parent based on their income. Enter the child support amount payable for each parent. "
			/>

			<p>
				Suggested calculation:
				<br />
				<br />
				The set-off is usually calculated by subtracting the lower child
				support payment from the higher child support payment. According
				to these numerical entries, {{ payorName }} should pay to
				{{ payeeName }} {{ setOff | formatCurrency }} per month.
			</p>

			<radio-group
				v-model="childSupport.setOffParty"
				input-cols="4"
				class="mb-3"
				label="The set-off will be paid by"
				select
				partner
				:agreement-data="agreementData"
				:testid="'child-support-set-off-party'"
				:length="'long'"
				:disabled="isViewMode"
				toolTipText="Divii lets you know who will pay the set-off amount.  Make sure this matches your calculation."
			/>

			<!-- <input-group
				v-model="childSupport.setOffAmount"
				label="The set-off is"
				dollar
				:testid="'child-support-set-off-amount'"
				:disabled="isViewMode"
			/> -->
			<b-form-group label="The set-off is">
				<b-input-group prepend="$">
					<InputNumber
						v-model="childSupport.setOffAmount"
						mode="decimal"
						:min-fraction-digits="2"
						:data-testid="'child-support-set-off-amount'"
						:placeholder="placeholder"
						pattern="[0-9]*"
						inputmode="numeric"
						:disabled="isViewMode"
						toolTipText="Divii calculates the set off for you.  Make sure this matches your calculation."
			/>
					<b-icon-info-circle
						v-if="isUserSeparatingParty"
						v-b-tooltip.html="'Divii calculates the set off for you.  Make sure this matches your calculation.'"
						class="ml-2 mt-2"
					>
					</b-icon-info-circle>
				</b-input-group>
			</b-form-group>
		</conditional-group>

		<radio-group
			v-model="childSupport.commencementTrigger"
			class="mt-2"
			select
			input-cols="8"
			reset-undefined
			label="On what date will Child Support commence?"
			:options="[
				{
					value: 'agreement',
					text: 'On a specified day, immediately following the Agreement being signed',
				},
				{
					value: 'house',
					text: 'On a specified day, immediately following the transfer of the Family Residence',
				},
				{ value: 'date', text: 'On a particular date' },
			]"
			:testid="'child-support-commencement-date-specification'"
			:length="'long'"
			:disabled="isViewMode"
			toolTipText="Typically, Child Support is paid on the first day of the month immediately following the Agrement
			being signed and will continue on the first of each month thereafter.  If you decided to reside together for a period
			and share household expenses, you can opt to have it payable after the transfer of the Family Residence.  Or if you rent
			and you know that you'll be moving out on a particular date, you can specify a particular date after that.  "
		/>

		<conditional-group
			class="mt-2"
			:condition="childSupport.commencementTrigger === 'agreement'"
		>
			<radio-group
				v-model="childSupport.commencementDay"
				class="mb-3 mt-3"
				:options="dayOptions"
				input-cols="2"
				label="On which day of the month?"
				reset-undefined
				select
				:testid="'child-support-commencement-month'"
				:disabled="isViewMode"
			/>
		</conditional-group>

		<conditional-group
			class="mt-2"
			:condition="childSupport.commencementTrigger === 'house'"
		>
			<radio-group
				v-model="childSupport.commencementDay"
				:options="dayOptions"
				input-cols="2"
				label="Day"
				reset-undefined
				select
				:testid="'child-support-commencement-day'"
				:disabled="isViewMode"
			/>
		</conditional-group>

		<conditional-group
			class="mt-2"
			:condition="childSupport.commencementTrigger === 'date'"
		>
			<date-input
				v-model="childSupport.commencementDate"
				input-cols="4"
				reset-undefined
				label="Date"
				:testid="'child-support-commencement-date'"
				:disabled="isViewMode"
			/>
		</conditional-group>

		<radio-group
			v-model="childSupport.paymentOptions"
			select
			class="mb-3 mt-4"
			input-cols="4"
			label="Child support will be paid in:"
			:options="['One payment', 'Two payments', 'Bi-weekly payments']"
			:testid="'child-support-payment-options'"
			:length="'long'"
			:disabled="isViewMode"
			toolTipText="Typically, Child Support is paid in one monthly payment, but sometimes,
			people prefer to be more specific and follow their bi-weekly paycheques or paid twice monthly."

		/>

		<conditional-group
			class="mt-3"
			:condition="childSupport.paymentOptions === 'One payment'"
			@reset="childSupport.firstPaymentDay = undefined"
		>
			<radio-group
				v-model="childSupport.firstPaymentDay"
				select
				class="mt-3 mb-2"
				input-cols="3"
				label="What day of each month will Child Support continue to be paid?"
				:options="dayOptions"
				:testid="'child-support-day-options'"
				:disabled="isViewMode"
			/>
		</conditional-group>

		<conditional-group
			class="mt-3"
			:condition="childSupport.paymentOptions === 'Two payments'"
			@reset="
				childSupport.firstPaymentDay = undefined;
				childSupport.secondPaymentDay = undefined;
			"
		>
			<radio-group
				v-model="childSupport.firstPaymentDay"
				select
				class="mb-3"
				input-cols="3"
				label="On what day of the month will the first Child Support payment continue to be paid?"
				:options="dayOptions"
				:testid="'child-support-pay-options-first-child'"
				:disabled="isViewMode"
			/>

			<radio-group
				v-model="childSupport.secondPaymentDay"
				select
				input-cols="3"
				label="On what day of the month will the second Child Support payment continue to be paid?"
				:options="dayOptions"
				:testid="'child-support-pay-options-second-child'"
				:disabled="isViewMode"
			/>
		</conditional-group>

		<yes-no-choice
			v-model="childSupport.reviewable"
			reset-undefined
			label="Is Child Support subject to review and variation?"
			class="mt-4"
			:testid="'child-support-reviewable'"
			:disabled="isViewMode"
			toolTipText="Under the <i>Federal Child Support Guidelines</i>, Child Support is reviewable every year and subject to variation.  
			It is recommended that you add this clause by answering 'yes' so that you have a process to follow each year.  
			This process includes what happens if you decide not to review."
		/>

		<conditional-group :condition="childSupport.reviewable">
			<radio-group
				v-model="childSupport.yearOfReview"
				class="mt-3"
				input-cols="4"
				select
				label="In what year will review commence?"
				:options="yearOptions"
				:testid="'child-support-review-year'"
				:disabled="isViewMode"
				toolTipText="The clause says that you will review each June (after income tax returns are completed).  
				You may choose to review the next June, or if that is only a few months away, you can opt for the year after.  "
			/>
		</conditional-group>

		<p
			v-if="parenting.arrangement === 'Shared' && childrenUnder18.length > 1"
			class="my-3"
		>
			Given that you've selected a shared parenting arrangement with
			multiple children, for the purposes of tax:
			<b-icon-info-circle class="ml-2"
				v-if="isUserSeparatingParty"
				v-b-tooltip="'Each parent may qualify to claim one child as a dependent.  In the event that you have more than two children, your youngest two children are selected as choices for the Dependent Tax Credit.'"
			>
			</b-icon-info-circle>
		</p>

		<conditional-group
			class="my-3"
			:condition="
				parenting.arrangement === 'Shared' && childrenUnder18.length > 1
			"
		>
			<radio-group
				v-model="childSupport.partner1TaxClaim"
				input-cols="4"
				class="mb-3"
				:label="`Which child will ${partner1Name} claim as a dependant`"
				select
				:options="childrenUnder18Names"
				:testid="'child-support-p1-claim-dependant'"
				:length="'long'"
				:disabled="isViewMode"
			/>

			<radio-group
				v-model="childSupport.partner2TaxClaim"
				input-cols="4"
				class="mb-3"
				:label="`Which child will ${partner2Name} claim as a dependant`"
				select
				:options="childrenUnder18Names"
				:testid="'child-support-p2-claim-dependant'"
				:length="'long'"
				:disabled="isViewMode"
			/>
		</conditional-group>
		<additional-clauses class="mt-2" :newLabel="'Add Clause to Recitals'" :title="'Additional Clauses into Recitals'" v-model="childSupport.additionalClausesToRecitals" :testid="'child-support-add-clause-to-recitals-'"
		:disabled="isViewMode"/>
		<additional-clauses class="mt-2" :newLabel="'Add Clause to Section'" :title="'Additional Clauses into Sections'" v-model="childSupport.additionalClauses" :testid="'child-support-add-clause-'"
		:disabled="isViewMode"/>
	</b-card>
</template>
